import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import type { Partner } from "@shared/schema";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Briefcase, Loader2, RefreshCw } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PartnerDetailView } from "./PartnerDetailView";

export function WholesalerPartnersPanel() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(null);
  const [syncingId, setSyncingId] = useState<number | null>(null);

  const wholesalersQuery = useQuery<Partner[]>({
    queryKey: ["/api/admin/partners", "wholesaler"],
    queryFn: async () => {
      const res = await fetch(`/api/admin/partners?partnerType=wholesaler`, { credentials: "include" });
      return res.json();
    },
  });

  const syncMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (partnerId: number) =>
      apiRequest("POST", `/api/admin/partners/${partnerId}/sync-airtable`, {}),
    onMutate: (partnerId: number) => setSyncingId(partnerId),
    onSettled: () => setSyncingId(null),
    onSuccess: (_data, partnerId) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/partners"] });
      toast({ title: "Sync complete", description: `Wholesaler #${partnerId} Airtable sync finished.` });
    },
    onError: (err: any) =>
      toast({ title: "Sync failed", description: parseErrorMessage(err, "Couldn't sync. Please try again."), variant: "destructive" }),
  });

  const allWholesalers = wholesalersQuery.data ?? [];
  const selectedPartner = allWholesalers.find(p => p.id === selectedPartnerId) ?? null;

  if (selectedPartner) {
    return <PartnerDetailView partner={selectedPartner} onBack={() => setSelectedPartnerId(null)} onUpdated={() => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/partners"] });
    }} />;
  }

  const isErrorStatus = (status: string): boolean => {
    const s = status.toLowerCase();
    return s.startsWith("error") || s.startsWith("auto-sync skipped") || s.includes(" errors)");
  };

  const formatRelative = (iso: string): string => {
    const ms = Date.parse(iso);
    if (!Number.isFinite(ms)) return iso;
    const diffSec = Math.max(0, Math.floor((Date.now() - ms) / 1000));
    if (diffSec < 60) return `${diffSec}s ago`;
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    return `${Math.floor(diffSec / 86400)}d ago`;
  };

  const erroringCount = allWholesalers.filter(p => {
    const d = (p.details as Record<string, unknown> | null) ?? {};
    const status = typeof d.airtableLastSyncStatus === "string" ? d.airtableLastSyncStatus : "";
    return status && isErrorStatus(status);
  }).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold">Wholesaler Partners</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Monitor each wholesaler's Airtable auto-sync status and trigger a manual sync when needed.</p>
        </div>
        <span className="text-xs text-muted-foreground" data-testid="text-wholesalers-summary">
          {allWholesalers.length} wholesaler{allWholesalers.length !== 1 ? "s" : ""}
          {erroringCount > 0 ? ` · ${erroringCount} erroring` : ""}
        </span>
      </div>
      <Card>
        {wholesalersQuery.isLoading ? (
          <div className="p-6 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
        ) : allWholesalers.length === 0 ? (
          <div className="p-12 text-center">
            <Briefcase className="mx-auto h-10 w-10 text-muted-foreground/40 mb-3" />
            <p className="text-muted-foreground">No wholesaler partners yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Auto-sync</TableHead>
                  <TableHead>Last sync</TableHead>
                  <TableHead>Last status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allWholesalers.map(p => {
                  const details = (p.details as Record<string, unknown> | null) ?? {};
                  const scheduleEnabled = details.airtableScheduleEnabled === true;
                  const scheduleHoursRaw = Number(details.airtableScheduleHours);
                  const scheduleHours = Number.isFinite(scheduleHoursRaw) && scheduleHoursRaw > 0 ? scheduleHoursRaw : 4;
                  const lastSyncAt = typeof details.airtableLastSyncAt === "string" ? details.airtableLastSyncAt : "";
                  const lastStatus = typeof details.airtableLastSyncStatus === "string" ? details.airtableLastSyncStatus : "";
                  const isErrored = !!lastStatus && isErrorStatus(lastStatus);
                  return (
                    <TableRow
                      key={p.id}
                      className={isErrored ? "bg-red-50 dark:bg-red-950/20" : undefined}
                      data-testid={`row-wholesaler-${p.id}`}
                    >
                      <TableCell>
                        <button className="font-medium text-left hover:underline" onClick={() => setSelectedPartnerId(p.id)} data-testid={`link-wholesaler-${p.id}`}>
                          {p.contactName}
                        </button>
                        <div className="text-xs text-muted-foreground">{p.email}</div>
                      </TableCell>
                      <TableCell>{p.companyName}</TableCell>
                      <TableCell data-testid={`text-autosync-${p.id}`}>
                        {scheduleEnabled ? (
                          <Badge className="text-xs bg-green-600">On · {scheduleHours}h</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">Off</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground" data-testid={`text-last-sync-${p.id}`}>
                        {lastSyncAt ? (
                          <span title={new Date(lastSyncAt).toLocaleString()}>{formatRelative(lastSyncAt)}</span>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className="text-xs max-w-md" data-testid={`text-last-status-${p.id}`}>
                        {lastStatus ? (
                          <span className={isErrored ? "text-red-700 dark:text-red-400" : "text-muted-foreground"}>{lastStatus}</span>
                        ) : (
                          <span className="text-muted-foreground">Never synced</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          disabled={syncingId === p.id || syncMutation.isPending}
                          onClick={() => syncMutation.mutate(p.id)}
                          data-testid={`button-sync-wholesaler-${p.id}`}
                        >
                          {syncingId === p.id ? (
                            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                          ) : (
                            <RefreshCw className="h-3.5 w-3.5 mr-1" />
                          )}
                          Sync now
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
